// ---------------------------------------------------------------------------
// Runtime environment helpers — the SPA's single env reader.
//
// The admin reads the shared Doppler keys the api reads (`API_BASE_URL`,
// `NODE_ENV`), never a `VITE_*` copy of them, and derives what it needs in
// `./env-derive`.
//
// Two sources, runtime first:
//   1. `window.__env__` — written by `entrypoint.sh` when the nginx image runs
//      (Docker path). On the Render static sites it is the empty `{}` shipped
//      in `public/__env__.js`, so the build-time values below apply.
//   2. `import.meta.env.<KEY>` — inlined by `vite.config.ts` `define` from the
//      build environment (Render's Doppler sync, the dev-server shell, `.env`).
//
// Always use these getters instead of reading `import.meta.env` directly so
// both sources are honoured.
// ---------------------------------------------------------------------------

import { apiBaseFrom, appEnvFrom } from './env-derive';

type RuntimeEnv = {
  API_BASE_URL?: string;
  NODE_ENV?: string;
  VITE_PRIMEUI_LICENSE_KEY?: string;
};

type SharedKey = 'API_BASE_URL' | 'NODE_ENV';

function getRuntimeEnv(): RuntimeEnv {
  if (typeof window === 'undefined') return {};
  return (window as unknown as { __env__?: RuntimeEnv }).__env__ ?? {};
}

/**
 * Build-time value of a shared key. Each read is a literal
 * `import.meta.env.<KEY>` member expression so Vite's `define` can replace it.
 */
function buildTimeValue(key: SharedKey): string | undefined {
  switch (key) {
    case 'API_BASE_URL':
      return import.meta.env.API_BASE_URL;
    case 'NODE_ENV':
      return import.meta.env.NODE_ENV;
  }
}

/** Runtime value when non-empty, else the build-time value. */
function readShared(key: SharedKey): string | undefined {
  return getRuntimeEnv()[key] || buildTimeValue(key) || undefined;
}

/** The api base the axios instance uses: `${API_BASE_URL}/v1`. */
export function getApiBase(): string {
  return apiBaseFrom(readShared('API_BASE_URL'));
}

/** The environment label (`development` | `staging` | `production`) from `NODE_ENV`. */
export function getAppEnv(): string | undefined {
  return appEnvFrom(readShared('NODE_ENV'));
}

/**
 * Resolve the PrimeUI (PrimeVue 5) licence key.
 *
 * Runtime-first like the others, so the nginx container picks the key up from
 * Doppler without a rebuild; the `import.meta.env` fallback covers the Render
 * static-site build, which has no entrypoint and inlines at build time.
 *
 * Read once at bootstrap in `main.ts` — see the note there on why an unset key
 * is deliberately left loud.
 */
export function getPrimeUiLicenseKey(): string | undefined {
  const runtime = getRuntimeEnv();
  return (
    runtime.VITE_PRIMEUI_LICENSE_KEY ||
    (import.meta.env.VITE_PRIMEUI_LICENSE_KEY as string | undefined)
  );
}
